// Code generated by apic; DO NOT EDIT.
import type { RestEndpointMeta } from "../types";
import type { SampleContext } from "./shared";
import { expandPath, isUnsafe, resolveAuthMode, hasMTLSLeaf } from "./shared";
export type { SampleContext } from "./shared";

// ── curl-specific helper ──────────────────────────────────────────────────────

/**
 * Escape a string for use inside a POSIX single-quoted shell argument.
 * Single quotes cannot appear inside a single-quoted string; the standard
 * POSIX idiom is to end the single-quoted section, emit an escaped single
 * quote, then re-open single quoting: s → '\''s'\''  (F-4 appsec).
 */
export function shellEscapeSQ(s: string): string {
  return s.replace(/'/g, "'\\''");
}

/**
 * curlSample returns a runnable curl command for the given endpoint and context.
 *
 * Auth injection rules:
 *  - jwt / cookie-bearer → Authorization: Bearer <token>
 *  - api_key             → X-API-Key: <api-key>
 *  - cookie + CSRF       → Bearer + X-CSRF-Token note (auto/required)
 *  - mtls / cac / piv    → comment only; no header (transport-layer cert)
 *  - composite with mtls leaf → dominant app-layer header + comment
 *  - public              → no auth header
 *
 * Never emits a real secret; uses ctx values or <token>/<api-key> placeholders.
 */
export function curlSample(ep: RestEndpointMeta, ctx: SampleContext): string {
  const method = ep.method.toUpperCase();
  const expandedPath = expandPath(ep.path, ctx);
  const url = ctx.baseUrl.replace(/\/$/, "") + expandedPath;

  const lines: string[] = [];

  lines.push(`curl -s \\`);
  lines.push(`  -X ${method} \\`);
  lines.push(`  '${shellEscapeSQ(url)}' \\`);
  lines.push(`  -H 'Accept: application/json' \\`);

  const mode = resolveAuthMode(ep.auth);
  const composite = ep.auth.composite !== null;
  const mtlsLeaf = hasMTLSLeaf(ep.auth);

  if (mode === "jwt" || mode === "cookie") {
    const tok = ctx.token !== undefined && ctx.token !== "" ? ctx.token : "<token>";
    lines.push(`  -H 'Authorization: Bearer ${shellEscapeSQ(tok)}' \\`);
    if (ep.auth.csrf === "required" || ep.auth.csrf === "auto") {
      lines.push(`  -H 'X-CSRF-Token: <csrf-token>' \\`);
    }
    if (composite && mtlsLeaf) {
      lines.push(`  # also requires a client certificate (mTLS — set via --cert / --key)`);
    }
  } else if (mode === "api_key") {
    const key = ctx.apiKey !== undefined && ctx.apiKey !== "" ? ctx.apiKey : "<api-key>";
    lines.push(`  -H 'X-API-Key: ${shellEscapeSQ(key)}' \\`);
    if (composite && mtlsLeaf) {
      lines.push(`  # also requires a client certificate (mTLS — set via --cert / --key)`);
    }
  } else if (mode === "mtls" || mode === "cac" || mode === "piv") {
    // Transport-layer certificate; no application-layer header.
    lines.push(`  # requires a client certificate — cannot be expressed as a header`);
    lines.push(`  # add: --cert /path/to/client.pem --key /path/to/client-key.pem \\`);
  }
  // public / none / empty → no auth header

  if (isUnsafe(method)) {
    const body = ctx.body !== undefined ? JSON.stringify(ctx.body) : "{}";
    lines.push(`  -H 'Content-Type: application/json' \\`);
    lines.push(`  -d '${shellEscapeSQ(body)}'`);
  } else {
    // Remove trailing backslash continuation from last meaningful line.
    const last = lines[lines.length - 1];
    if (last !== undefined && last.endsWith(" \\")) {
      lines[lines.length - 1] = last.slice(0, -2);
    }
  }

  return lines.join("\n");
}
